import { type FC, useId } from 'react';

import type { CalculatorInputField } from 'components/Calculator/config/types';

interface CalculatorFieldProps {
  field: CalculatorInputField;
  onChange: (key: string, value: string) => void;
  value: string;
}

const AFFIX_CLASSNAME = 'flex shrink-0 items-center bg-primary-100 px-3 text-md font-medium text-primary-900';

/**
 * One input row: label above, then the field with its optional unit chips.
 *
 * The input is type="text" with inputMode="decimal" rather than type="number".
 * A number input silently swallows what it considers invalid, so a reader
 * mid-way through typing "-" or "1." sees the value vanish, and its scroll
 * wheel behaviour changes figures when someone is only scrolling past the post.
 */
const CalculatorField: FC<CalculatorFieldProps> = ({ field, onChange, value }) => {
  // useId gives each instance its own ids, so several calculators in one post
  // never share a label association.
  const inputId = useId();
  const { key, label, prefixLabel, suffixLabel } = field;

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-primary-900" htmlFor={inputId}>
        {label}
      </label>
      <div className="flex overflow-hidden rounded-lg border-[1.5px] border-solid border-primary-200 bg-primary-25 focus-within:border-primary-500">
        {prefixLabel !== undefined && prefixLabel !== '' && <span className={AFFIX_CLASSNAME}>{prefixLabel}</span>}
        <input
          className="w-full min-w-0 bg-transparent px-3.5 py-2.5 text-lg font-semibold text-gray-900 focus-visible:outline-none"
          id={inputId}
          inputMode="decimal"
          onChange={event => onChange(key, event.target.value)}
          type="text"
          value={value}
        />
        {suffixLabel !== undefined && suffixLabel !== '' && <span className={AFFIX_CLASSNAME}>{suffixLabel}</span>}
      </div>
    </div>
  );
};

export default CalculatorField;
